import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Wallet as WalletIcon, Coins, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

const METHODS = [
  { id: "paypal", label: "PayPal", hint: "PayPal email address" },
  { id: "bank",   label: "Bank Transfer", hint: "Routing + account number, account name" },
  { id: "wipay",  label: "WiPay (Caribbean)", hint: "WiPay account email/phone" },
];

export default function CreatorEarnings() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [pearls, setPearls] = useState(500);
  const [method, setMethod] = useState("paypal");
  const [dest, setDest] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/creator/earnings");
      setData(data);
      setPearls(Math.max(500, Math.min(data.pearls_balance || 500, 5000)));
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const request = async () => {
    if (!dest.trim()) { toast.error("Please add destination details"); return; }
    setBusy(true);
    try {
      await api.post("/creator/payout-request", { pearls, method, destination: dest.trim() });
      toast.success("Payout requested! Admin reviews within 48h.");
      setDest("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="min-h-[100dvh] bg-ink flex items-center justify-center text-cream/40 text-xs tracking-[0.3em] uppercase animate-pulse">Loading…</div>;

  return (
    <div className="min-h-[100dvh] bg-ink pb-32" data-testid="creator-earnings">
      <div className="px-5 pt-6 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream"><ArrowLeft size={18} /></button>
        <div>
          <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Earnings</p>
          <h1 className="text-cream text-3xl font-black tracking-tighter">Cash out pearls</h1>
        </div>
      </div>

      {/* Balance card */}
      <div className="mx-5 mt-6 rounded-3xl bg-gradient-to-br from-papaya/15 via-white/5 to-jam-gold/10 border border-papaya/30 p-5">
        <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-black">Available balance</p>
        <p className="text-cream text-5xl font-black tracking-tighter mt-1">{data.pearls_balance.toLocaleString()} <span className="text-2xl text-cream/60">pearls</span></p>
        <p className="text-cream/50 text-xs mt-1">≈ ${data.usd_estimate.toFixed(2)} USD · Lifetime: {data.pearls_lifetime.toLocaleString()} pearls</p>
      </div>

      {/* Breakdown */}
      {Object.keys(data.breakdown || {}).length > 0 && (
        <section className="px-5 mt-6">
          <p className="text-cream/60 text-[10px] tracking-[0.3em] uppercase font-bold mb-2">Sources</p>
          <div className="space-y-2">
            {Object.entries(data.breakdown).map(([kind, info]) => (
              <div key={kind} className="flex items-center justify-between p-3 rounded-2xl bg-white/5 border border-white/10">
                <p className="text-cream text-sm">{kind.replace(/_/g, " ")}</p>
                <p className="text-cream font-bold">{info.pearls.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Request payout */}
      <section className="px-5 mt-8">
        <p className="text-cream/60 text-[10px] tracking-[0.3em] uppercase font-bold mb-3">Request payout</p>
        <div className="space-y-3">
          <div>
            <label className="text-cream/50 text-[10px] tracking-[0.2em] uppercase font-bold mb-1 block">Amount (pearls — min 500)</label>
            <input
              type="number"
              min={500}
              max={data.pearls_balance}
              data-testid="payout-pearls"
              value={pearls}
              onChange={(e) => setPearls(parseInt(e.target.value, 10) || 0)}
              className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-cream text-sm focus:outline-none focus:border-papaya"
            />
            <p className="text-cream/50 text-[11px] mt-1">≈ ${(pearls / 100).toFixed(2)} USD</p>
          </div>

          <div>
            <label className="text-cream/50 text-[10px] tracking-[0.2em] uppercase font-bold mb-1 block">Method</label>
            <div className="grid grid-cols-3 gap-2" data-testid="payout-methods">
              {METHODS.map((m) => (
                <button
                  key={m.id}
                  data-testid={`payout-method-${m.id}`}
                  onClick={() => setMethod(m.id)}
                  className={`p-3 rounded-2xl text-left transition-colors ${method === m.id ? "bg-papaya/10 border-2 border-papaya text-cream" : "bg-white/5 border-2 border-transparent text-cream/60"}`}
                >
                  <p className="font-black text-[11px]">{m.label}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-cream/50 text-[10px] tracking-[0.2em] uppercase font-bold mb-1 block">Destination</label>
            <input
              data-testid="payout-destination"
              value={dest}
              onChange={(e) => setDest(e.target.value)}
              placeholder={METHODS.find((m) => m.id === method)?.hint}
              className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-papaya"
            />
          </div>

          <button
            onClick={request}
            disabled={busy || pearls < 500 || pearls > data.pearls_balance}
            data-testid="payout-submit"
            className="w-full py-3.5 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:bg-papaya-hover papaya-glow flex items-center justify-center gap-2 disabled:opacity-40"
          >
            <PaperPlaneTilt size={16} weight="fill" /> Request payout
          </button>
        </div>
      </section>

      {/* Pending */}
      {data.pending_payouts.length > 0 && (
        <section className="px-5 mt-8">
          <p className="text-cream/60 text-[10px] tracking-[0.3em] uppercase font-bold mb-2">Pending</p>
          <div className="space-y-2">
            {data.pending_payouts.map((p) => (
              <div key={p.id} className="p-3 rounded-2xl bg-white/5 border border-white/10">
                <p className="text-cream font-bold text-sm">{p.pearls} pearls · ${p.usd_amount}</p>
                <p className="text-cream/50 text-[11px]">{p.method} → {p.destination}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
